import React, { useState, useEffect } from "react";
import { Box, Button, Typography, Chip, Tooltip } from "@mui/material";
import { textAnswerRetryManager } from "../../utils/programming/textAnswerRetryManager";
import { updateCandidateResult } from "../../services/assessment.api";

/**
 * Component to display retry status and manual retry button for text answers
 * @param {string} questionId - The question ID
 * @param {string} questionType - The question type (mcq, subjective, programming)
 * @param {function} onRetrySuccess - Callback when retry succeeds
 * @param {function} onRetryError - Callback when retry fails
 */
const TextAnswerRetryStatus = ({
  questionId,
  questionType,
  onRetrySuccess,
  onRetryError,
}) => {
  const [retryStatus, setRetryStatus] = useState(null);
  const [isRetrying, setIsRetrying] = useState(false);
  const [isManualRetrying, setIsManualRetrying] = useState(false);

  // Check retry status periodically
  useEffect(() => {
    if (!questionId) return;

    const checkStatus = () => {
      const status = textAnswerRetryManager.getRetryStatus(questionId);
      setRetryStatus(status);
      setIsRetrying(textAnswerRetryManager.isRetrying(questionId));
    };

    // Check immediately
    checkStatus();

    // Check every 2 seconds
    const interval = setInterval(checkStatus, 2000);

    return () => clearInterval(interval);
  }, [questionId]);

  // Handle manual retry
  const handleManualRetry = async () => {
    if (!questionId || isManualRetrying) return;

    setIsManualRetrying(true);
    try {
      const result = await textAnswerRetryManager.manualRetry(
        questionId,
        (screeningId, payload) => updateCandidateResult(screeningId, payload)
      );

      if (result.success) {
        setRetryStatus(null);
        setIsRetrying(false);
        if (onRetrySuccess) {
          onRetrySuccess(result);
        }
      }
    } catch (error) {
      console.error("Manual retry failed:", error);
      if (onRetryError) {
        onRetryError(error);
      }
    } finally {
      setIsManualRetrying(false);
    }
  };

  // Don't render if no retry status
  if (!retryStatus && !isRetrying) {
    return null;
  }

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1,
        padding: "8px 12px",
        backgroundColor:
          retryStatus?.status === "failed" ? "#ffebee" : "#e3f2fd",
        borderRadius: "4px",
        border: `1px solid ${
          retryStatus?.status === "failed" ? "#f44336" : "#2196f3"
        }`,
        marginTop: 1,
      }}
    >
      {isRetrying && (
        <>
          <Box
            sx={{
              width: "16px",
              height: "16px",
              border: "2px solid #2196f3",
              borderTop: "2px solid transparent",
              borderRadius: "50%",
              animation: "spin 1s linear infinite",
              "@keyframes spin": {
                "0%": { transform: "rotate(0deg)" },
                "100%": { transform: "rotate(360deg)" },
              },
            }}
          />
          <Typography sx={{ fontSize: "12px", color: "#1976d2" }}>
            Retrying submission... (Attempt {retryStatus?.attempts || 0}/
            {retryStatus?.maxAttempts || 3})
          </Typography>
        </>
      )}

      {retryStatus?.status === "failed" && (
        <>
          <Chip
            label="Failed"
            size="small"
            sx={{
              backgroundColor: "#f44336",
              color: "white",
              fontSize: "11px",
              height: "20px",
            }}
          />
          <Typography sx={{ fontSize: "12px", color: "#d32f2f", flex: 1 }}>
            Submission failed after {retryStatus.attempts} attempts
          </Typography>
          <Tooltip title="Retry submission manually">
            <Button
              variant="contained"
              size="small"
              onClick={handleManualRetry}
              disabled={isManualRetrying}
              sx={{
                minWidth: "80px",
                height: "28px",
                fontSize: "11px",
                backgroundColor: "#f44336",
                "&:hover": { backgroundColor: "#d32f2f" },
              }}
            >
              {isManualRetrying ? "Retrying..." : "Retry"}
            </Button>
          </Tooltip>
        </>
      )}

      {retryStatus?.status === "pending" && (
        <Typography sx={{ fontSize: "12px", color: "#1976d2" }}>
          Answer queued. Will retry automatically when connection is restored.
        </Typography>
      )}
    </Box>
  );
};

export default TextAnswerRetryStatus;
